import ControlledTable from "@/components/controlled-table";
import { InfoIcon } from "@/components/icons";
import useApi from "@/hooks/useApi";
import { HeaderCell } from "@/components/ui/table";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import {
  IMonthlyLeadSummaryRow,
  TMonthlyLeadSummaryResponse,
} from "@/utilities/types/MonthlyLeadSummary";
import { useDebounce } from "ahooks";
import dayjs from "dayjs";
import { ColumnsType } from "rc-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { PiMagnifyingGlassBold } from "react-icons/pi";
import { ActionIcon, Input, Select, Text, Title, Tooltip } from "rizzui";
import { SortConfig } from "@/hooks/useTableServer";

interface IMonthlyLeadSummaryTableRow extends IMonthlyLeadSummaryRow {
  id: string;
  qualifiedVerifiedLeads: number;
}

type TMonthlyLeadSummarySortBy =
  | "userName"
  | "totalLeads"
  | "totalVerifiedLeads"
  | "qualifiedVerifiedLeads";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const month = index + 1;

  return {
    label: dayjs().month(index).format("MMMM"),
    value: month,
  };
});

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, index) => {
  const year = dayjs().year() - index;

  return {
    label: `${year}`,
    value: year,
  };
});

function getRows(
  response: TMonthlyLeadSummaryResponse | null,
  sortConfig: SortConfig<TMonthlyLeadSummarySortBy>,
): IMonthlyLeadSummaryTableRow[] {
  const data = response?.data;
  const items = Array.isArray(data) ? data : data?.items ?? [];

  const rows = items.map((item, index) => ({
    ...item,
    id: item.id ?? item.userId ?? `${item.userName}-${index}`,
    qualifiedVerifiedLeads: item.qualifiedVerifiedLeads,
  }));

  return rows.sort((firstRow, secondRow) => {
    const firstValue = firstRow[sortConfig.key];
    const secondValue = secondRow[sortConfig.key];

    if (typeof firstValue === "string" && typeof secondValue === "string") {
      const comparison = firstValue.localeCompare(secondValue);

      return sortConfig.direction === "asc" ? comparison : -comparison;
    }

    const normalizedFirstValue =
      typeof firstValue === "number" ? firstValue : 0;
    const normalizedSecondValue =
      typeof secondValue === "number" ? secondValue : 0;
    const comparison = normalizedFirstValue - normalizedSecondValue;

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
}

function getPaginationData(response: TMonthlyLeadSummaryResponse | null) {
  const data = response?.data;

  if (Array.isArray(data)) {
    return {
      total: data.length,
      currentPage: 1,
      limit: data.length || 10,
    };
  }

  return {
    total: data?.total ?? 0,
    currentPage: data?.currentPage ?? 1,
    limit: data?.limit ?? 10,
  };
}

export default function MonthlyLeadSummaryTable() {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState<
    SortConfig<TMonthlyLeadSummarySortBy>
  >({
    key: "totalLeads",
    direction: "desc",
  });
  const debouncedSearchTerm = useDebounce(searchTerm, { wait: 500 });

  const {
    loading,
    request: getMonthlyLeadSummary,
    response,
  } = useApi<TMonthlyLeadSummaryResponse>({
    request: EnquiryEntriesRepository.getMonthlyLeadSummary,
  });

  useEffect(() => {
    getMonthlyLeadSummary({
      month: selectedMonth,
      year: selectedYear,
      page: currentPage,
      limit: pageSize,
      userName: debouncedSearchTerm.trim() || undefined,
      sort: {
        [sortConfig.key]: sortConfig.direction.toUpperCase() as "ASC" | "DESC",
      },
    }).catch((error) => {
      console.error("Failed to fetch monthly lead summary:", error);
      toast.error("Failed to fetch monthly lead summary");
    });
  }, [
    currentPage,
    debouncedSearchTerm,
    getMonthlyLeadSummary,
    pageSize,
    selectedMonth,
    selectedYear,
    sortConfig.direction,
    sortConfig.key,
  ]);

  const rows = useMemo(() => getRows(response, sortConfig), [response, sortConfig]);
  const paginationData = useMemo(() => getPaginationData(response), [response]);
  const selectedMonthOption = useMemo(
    () =>
      MONTH_OPTIONS.find((option) => option.value === selectedMonth) ?? null,
    [selectedMonth],
  );
  const selectedYearOption = useMemo(
    () => YEAR_OPTIONS.find((option) => option.value === selectedYear) ?? null,
    [selectedYear],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    pageSize,
    selectedMonth,
    selectedYear,
    sortConfig.direction,
    sortConfig.key,
  ]);

  const handleSort = useCallback(
    (key: TMonthlyLeadSummarySortBy) => {
      let direction: "asc" | "desc" = "asc";

      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "desc";
      }

      setSortConfig({ key, direction });
    },
    [sortConfig.direction, sortConfig.key],
  );

  const onHeaderCellClick = useCallback(
    (key: TMonthlyLeadSummarySortBy) => ({
      onClick: () => {
        handleSort(key);
      },
    }),
    [handleSort],
  );

  const columns: ColumnsType<IMonthlyLeadSummaryTableRow> = useMemo(
    () => [
      {
        title: (
          <HeaderCell
            title="User Name"
            sortable
            ascending={
              sortConfig.direction === "asc" && sortConfig.key === "userName"
            }
            isSortedBy={sortConfig.key === "userName"}
          />
        ),
        dataIndex: "userName",
        key: "userName",
        onHeaderCell: () => onHeaderCellClick("userName"),
        render: (value: string) => value || "-",
      },
      {
        title: (
          <HeaderCell
            title="Total Leads"
            sortable
            align="right"
            ascending={
              sortConfig.direction === "asc" && sortConfig.key === "totalLeads"
            }
            isSortedBy={sortConfig.key === "totalLeads"}
          />
        ),
        dataIndex: "totalLeads",
        key: "totalLeads",
        align: "right",
        onHeaderCell: () => onHeaderCellClick("totalLeads"),
        render: (value: number) => value ?? 0,
      },
      {
        title: (
          <HeaderCell
            title="Total Verified Leads"
            sortable
            align="right"
            ascending={
              sortConfig.direction === "asc" &&
              sortConfig.key === "totalVerifiedLeads"
            }
            isSortedBy={sortConfig.key === "totalVerifiedLeads"}
          />
        ),
        dataIndex: "totalVerifiedLeads",
        key: "totalVerifiedLeads",
        align: "right",
        onHeaderCell: () => onHeaderCellClick("totalVerifiedLeads"),
        render: (value: number) => value ?? 0,
      },
      {
        title: (
          <HeaderCell
            title={
              <div className="flex items-center justify-end gap-1">
                <span>Qualified Verified Leads</span>
                <Tooltip
                  size="sm"
                  content={
                    <div className="w-56 text-start">
                      Verified leads only, excluding spam and agent leads.
                    </div>
                  }
                  placement="top"
                  color="invert"
                  className="max-w-[300px]"
                >
                  <ActionIcon size="sm" variant="text">
                    <InfoIcon className="h-4 w-4 text-gray-400" />
                  </ActionIcon>
                </Tooltip>
              </div>
            }
            sortable
            align="right"
            ascending={
              sortConfig.direction === "asc" &&
              sortConfig.key === "qualifiedVerifiedLeads"
            }
            isSortedBy={sortConfig.key === "qualifiedVerifiedLeads"}
          />
        ),
        dataIndex: "qualifiedVerifiedLeads",
        key: "qualifiedVerifiedLeads",
        align: "right",
        onHeaderCell: () => onHeaderCellClick("qualifiedVerifiedLeads"),
        render: (value: number) => value ?? 0,
      },
    ],
    [onHeaderCellClick, sortConfig.direction, sortConfig.key],
  );

  return (
    <div className="mt-8 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <Title as="h4" className="text-lg font-semibold text-gray-900">
            Monthly Lead Summary
          </Title>
          <Text className="mt-1 text-sm text-gray-500">
            Summary for{" "}
            {dayjs().month(selectedMonth - 1).year(selectedYear).format(
              "MMMM YYYY",
            )}
          </Text>
        </div>

        <div className="flex flex-col gap-2 lg:flex-row">
          <Input
            type="search"
            placeholder="Search by user name"
            value={searchTerm}
            onClear={() => setSearchTerm("")}
            onChange={(event) => setSearchTerm(event.target.value)}
            prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
            clearable
            className="w-full lg:w-64"
          />
          <Select
            options={MONTH_OPTIONS}
            value={selectedMonthOption}
            onChange={(option: { value: number }) => {
              setSelectedMonth(option.value);
            }}
            className="w-full sm:w-44"
            placeholder="Select month"
          />
          <Select
            options={YEAR_OPTIONS}
            value={selectedYearOption}
            onChange={(option: { value: number }) => {
              setSelectedYear(option.value);
            }}
            className="w-full sm:w-32"
            placeholder="Select year"
          />
        </div>
      </div>

      <ControlledTable<IMonthlyLeadSummaryTableRow>
        variant="modern"
        data={rows}
        isLoading={loading}
        showLoadingText={true}
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: setPageSize,
          total: paginationData.total,
          current: paginationData.currentPage,
          onChange: (page: number) => setCurrentPage(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
